import { TradeMessage } from '../types/trade';

export interface GroupedTrade {
  th_id: number;
  trades_id: number;
  leagueid: number;
  time_stamp: string;
  is_accepted: boolean;
  is_refused: boolean;
  sender_username: string;
  sender_user_id: number;
  receiver_username: string;
  receiver_user_id: number;
  trades: {
    // Player fields
    sender_player_name: string | null;
    sender_player_id: number | null;
    sender_goals: number | null;
    sender_assists: number | null;
    sender_points: number | null;
    sender_salary: number | null;
    sender_yearleft: string | null;
    sender_isnhl: boolean | null;
    sender_salary_retain_pct: number | null;
    // Goalie fields
    sender_goalie_name: string | null;
    sender_goalie_id: number | null;
    sender_goalie_wins: number | null;
    sender_goalie_savepct: number | null;
    sender_goalie_shutout: number | null;
    sender_goalie_salary: number | null;
    sender_goalie_yearleft: string | null;
    // Draft pick fields
    sender_pick_id: number | null;
    sender_pick_description: string | null;
    sender_pick_original_user_id: number | null;
    sender_pick_original_username: string | null;
    // Receiver player fields
    receiver_player_name: string | null;
    receiver_player_id: number | null;
    receiver_goals: number | null;
    receiver_assists: number | null;
    receiver_points: number | null;
    receiver_salary: number | null;
    receiver_yearleft: string | null;
    receiver_isnhl: boolean | null;
    receiver_salary_retain_pct: number | null;
    // Receiver goalie fields
    receiver_goalie_name: string | null;
    receiver_goalie_id: number | null;
    receiver_goalie_wins: number | null;
    receiver_goalie_savepct: number | null;
    receiver_goalie_shutout: number | null;
    receiver_goalie_salary: number | null;
    receiver_goalie_yearleft: string | null;
    // Receiver draft pick fields
    receiver_pick_id: number | null;
    receiver_pick_description: string | null;
    receiver_pick_original_user_id: number | null;
    receiver_pick_original_username: string | null;
  }[];
}

export function groupTradesByTransaction(trades: TradeMessage[]): GroupedTrade[] {
  // Group trades by trades_id or th_id if trades_id is null
  const groupedByTradesId = trades.reduce((acc: { [key: string]: TradeMessage[] }, trade) => {
    const groupKey = trade.trades_id || trade.th_id;
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(trade);
    return acc;
  }, {});

  // Convert grouped trades to GroupedTrade format
  const groupedTrades = Object.values(groupedByTradesId).map(tradeGroup => {
    const firstTrade = tradeGroup[0];
    return {
      th_id: firstTrade.th_id,
      trades_id: firstTrade.trades_id || firstTrade.th_id,
      leagueid: firstTrade.leagueid,
      time_stamp: firstTrade.time_stamp,
      is_accepted: firstTrade.is_accepted,
      is_refused: firstTrade.is_refused,
      sender_username: firstTrade.sender_username,
      sender_user_id: firstTrade.sender_user_id,
      receiver_username: firstTrade.receiver_username,
      receiver_user_id: firstTrade.receiver_user_id,
      trades: tradeGroup.map(trade => ({
        sender_player_name: trade.sender_player_name,
        sender_player_id: trade.sender_player_id,
        sender_goals: trade.sender_goals,
        sender_assists: trade.sender_assists,
        sender_points: trade.sender_points,
        sender_salary: trade.sender_salary,
        sender_yearleft: trade.sender_yearleft,
        sender_isnhl: trade.sender_isnhl,
        sender_salary_retain_pct: trade.sender_salary_retain_pct,
        sender_goalie_name: trade.sender_goalie_name,
        sender_goalie_id: trade.sender_goalie_id,
        sender_goalie_wins: trade.sender_goalie_wins,
        sender_goalie_savepct: trade.sender_goalie_savepct,
        sender_goalie_shutout: trade.sender_goalie_shutout,
        sender_goalie_salary: trade.sender_goalie_salary,
        sender_goalie_yearleft: trade.sender_goalie_yearleft,
        sender_pick_id: trade.sender_pick_id,
        sender_pick_description: trade.sender_pick_description,
        sender_pick_original_user_id: trade.sender_pick_original_user_id,
        sender_pick_original_username: trade.sender_pick_original_username,
        receiver_player_name: trade.receiver_player_name,
        receiver_player_id: trade.receiver_player_id,
        receiver_goals: trade.receiver_goals,
        receiver_assists: trade.receiver_assists,
        receiver_points: trade.receiver_points,
        receiver_salary: trade.receiver_salary,
        receiver_yearleft: trade.receiver_yearleft,
        receiver_isnhl: trade.receiver_isnhl,
        receiver_salary_retain_pct: trade.receiver_salary_retain_pct,
        receiver_goalie_name: trade.receiver_goalie_name,
        receiver_goalie_id: trade.receiver_goalie_id,
        receiver_goalie_wins: trade.receiver_goalie_wins,
        receiver_goalie_savepct: trade.receiver_goalie_savepct,
        receiver_goalie_shutout: trade.receiver_goalie_shutout,
        receiver_goalie_salary: trade.receiver_goalie_salary,
        receiver_goalie_yearleft: trade.receiver_goalie_yearleft,
        receiver_pick_id: trade.receiver_pick_id,
        receiver_pick_description: trade.receiver_pick_description,
        receiver_pick_original_user_id: trade.receiver_pick_original_user_id,
        receiver_pick_original_username: trade.receiver_pick_original_username,
      }))
    };
  });

  // Sort by pending status and timestamp
  return groupedTrades.sort((a, b) => {
    // First sort by pending status (pending trades first)
    if (!a.is_accepted && !a.is_refused && (b.is_accepted || b.is_refused)) return -1;
    if ((a.is_accepted || a.is_refused) && !b.is_accepted && !b.is_refused) return 1;
    
    // Then sort by timestamp (most recent first)
    return new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime();
  });
}